import type { Delivery, IDeliveryLine, InventoryLevel, UntrackedMaterial } from '../interfaces/ILogistics.js';
import type { IStockPrefill } from './stockPrefill.js';
import { arrivedSince, withArrival, type ArrivedSince } from './deliveryAmounts.js';
import { firstLine, openLineFor } from './lines.js';
import { fold } from '../utils/text.js';
import { estimatedOnHand } from './onHand.js';

/** Everything `LogisticsPage` needs to open the stock dialog — the prefilled
 *  form plus the reconciliation context (what arrived, what to ask for,
 *  which order the save should mark delivered). */
export type IStockDialogState = {
  prefill: IStockPrefill | null;
  mode?: 'update' | 'sync';
  arrived?: ArrivedSince | null;
  estimatedRemaining?: number;
  /** Delivery to mark arrived when the dialog's Save is confirmed — it rides
   *  the snapshot's request (`syncArrivals`), never on open or cancel. */
  deliveredOnSave?: string;
};

/** A stock row tapped directly (not a Sync / Renew prompt) — a plain edit of
 *  the existing count, seeded with what the row already holds. */
export function buildRowClickStockDialog(level: InventoryLevel): IStockDialogState {
  return {
    prefill: {
      materialName: level.materialName,
      unit: level.unit,
      amount: level.amount,
      spanDays: level.spanDays,
      estimatedLeadTimeDays: level.estimatedLeadTimeDays,
      leadSource: level.leadSource,
      leadTimeDays: level.leadTimeDays,
      // What the material already holds, so a carried-forward figure is not
      // mistaken for something the system estimated.
      previous: {
        unit: level.unit,
        amount: level.amount,
        spanDays: level.spanDays,
        estimatedLeadTimeDays: level.estimatedLeadTimeDays,
      },
    },
  };
}

/* The prefill both Sync entry points share. `amount` is the STOCK's, not a
   delivery's: `learnedSpanDays` describes how long THIS amount lasts and the
   dialog derives its rate as amount / learnedSpanDays, so seeding a delivery
   quantity would pair two unrelated numerators. The arrival itself rides in
   `arrived`. `previous` records what the material already holds, so a
   carried-forward figure is not mistaken for something the system estimated;
   the suggested lead is what the landed delivery implies, offered for the
   field while the row's committed lead stays put until the Sync lands. */
function syncPrefill(level: InventoryLevel, spanDays: number): IStockPrefill {
  return {
    materialName: level.materialName,
    unit: level.unit,
    amount: level.amount,
    spanDays,
    estimatedLeadTimeDays: level.estimatedLeadTimeDays,
    learnedSpanDays: level.suggestedSpanDays,
    leadSource: level.leadSource,
    leadTimeDays: level.leadTimeDays,
    suggestedLeadTimeDays: level.suggestedLeadTimeDays,
    suggestedLeadSource: level.suggestedLeadSource,
    previous: {
      unit: level.unit,
      amount: level.amount,
      spanDays: level.spanDays,
      estimatedLeadTimeDays: level.estimatedLeadTimeDays,
    },
  };
}

// Renewing never writes directly — Delivery carries no quantity, and
// leftover stock merged into the new pile means only the person
// standing there knows the real total. Opens the stock dialog
// prefilled instead (spec §4).
/* A delivery landed on stock that still had cover. Both amounts are on
   site, so the dialog opens with the merge worked out: what arrived is
   a fact from the delivery record, what is LEFT is asked for. The
   seeded remaining is `dailyRate × daysLeft` — the estimate restated —
   which is why the field exists to be corrected rather than confirmed. */
/* Yes, it landed. The ORDER of the two records matters twice over.

   Marking it on click was the first attempt and was wrong: cancelling
   the dialog left an order recorded as arrived with nothing
   reconciled against it. So the arrival waits for a confirmed Save
   (`deliveredOnSave`), and rides the snapshot's own request
   (`syncArrivals`) — a refused count marks nothing.

   WITHIN that request the server records the arrival first: it stamps
   `deliveredAt` as an honest `now`, and an arrival stamped ~50ms
   AFTER the count it had just been merged into made the row see a
   fresh arrival on fresh stock and ask to Sync the same delivery
   again, forever. */
export function buildRenewArrivedStockDialog(
  level: InventoryLevel,
  deliveries: Delivery[],
): IStockDialogState {
  const inFlight = openLineFor(deliveries, level.materialName);
  return {
    prefill: syncPrefill(level, level.suggestedSpanDays ?? level.spanDays),
    mode: 'sync',
    // The LINE for this material, not the truck's total: a three-material
    // delivery adds its rebar to the rebar count and nothing else.
    arrived: inFlight === null ? null : withArrival(null, inFlight.order, inFlight.line),
    // Asked for rather than assumed, but prefilled with the obvious
    // answer for the row's own state: a ran-out row has nothing left,
    // while a COVERED row (the due-reached ask also fires there —
    // validated 2026-08-11) seeds the estimate the way Sync does.
    // Prefilling 0 on a covered row would claim the yard is bare
    // when the row itself says days of cover remain.
    estimatedRemaining: estimatedOnHand(level),
    // Marked delivered only once the stock write lands. Doing it on
    // click meant answering "Sync" and then cancelling left the order
    // recorded as arrived with nothing reconciled against it — a write
    // performed on intent rather than on confirmation.
    deliveredOnSave: inFlight?.order.ref,
  };
}

export function buildSyncStockDialog(level: InventoryLevel, deliveries: Delivery[]): IStockDialogState {
  return {
    prefill: syncPrefill(level, level.spanDays),
    mode: 'sync',
    arrived: arrivedSince(deliveries, level.materialName, level.enteredAt),
    /* A row that has run out is this same reconciliation with nothing
       left. Prefilled 0 so the common case is still one click through,
       but ASKED — assuming it is what made every renewal record exactly
       `spanDays` and turned the learned rate into an echo. */
    estimatedRemaining: estimatedOnHand(level),
  };
}

/* Everything the delivery already knows is carried over; only the span
   is left to type, because only a human knows how long it should last.
   No `previous` block — there is nothing prior to carry forward, and a
   value copied from the order is not an estimate this code made. */
export function buildAddStockDialog(m: UntrackedMaterial): IStockDialogState {
  return {
    prefill: {
      materialName: m.materialName,
      ...(m.unit != null ? { unit: m.unit } : {}),
      ...(m.quantity != null ? { amount: m.quantity } : {}),
      // The material's OWN measured lead when its deliveries yield
      // one. The dialog otherwise reads leads off the levels, and an
      // untracked material has none — so a material whose delivery
      // had just landed was shown the site average under a caption
      // saying it had no delivery history.
      ...(m.leadTimeDays != null
        ? { leadTimeDays: m.leadTimeDays, leadSource: m.leadSource ?? 'site-median' }
        : {}),
    },
    // Counting the material IS the answer to "Order arrived?" — you
    // cannot count what has not turned up. So recording the stock
    // also settles the order that has been sitting past its due date
    // with nobody willing to tick it.
    //
    // On save, never on click: the same rule the tracked Sync flow
    // follows. Marking it here would let a cancelled dialog leave an
    // order recorded as arrived with nothing reconciled against it.
    ...(m.deliveredAt == null ? { deliveredOnSave: m.deliveryRef } : {}),
  };
}

/**
 * An arrived line of a material with no stock yet, opened for its FIRST count:
 * the Sync reconciliation with nothing left before it (0, never asked — there
 * was no stock) and this line as the arrival, so the new total is the line's
 * amount and only the span is left to type.
 *
 * Built from the opened line, not from the material's untracked entry: the
 * amount and unit are the ones this truck brought. And it never carries
 * `deliveredOnSave` — the order is already in, so the count arrives nothing.
 */
export function buildFirstCountDialog(material: UntrackedMaterial, delivery: Delivery, line: IDeliveryLine): IStockDialogState {
  const { prefill } = buildAddStockDialog({ ...material, quantity: line.quantity, unit: line.unit });
  return { prefill, mode: 'sync', estimatedRemaining: 0, arrived: withArrival(null, delivery, line) };
}

/**
 * An order that has just been moved into Delivered, opened for reconciliation.
 *
 * The move itself never writes stock. A delivery says what was ORDERED; only
 * the person standing in the yard knows what is now on site — leftovers from
 * the old pile are merged into the new one, and the recorded amount is often
 * missing (Genba supplies none). So the arrival opens the same dialog the
 * inventory row's Sync opens and asks, rather than adding a number nobody
 * checked.
 *
 * Two shapes, one for each thing the material can be:
 *   tracked   → the row's own Sync, with this arrival guaranteed to be in it.
 *   untracked → a plain stock entry seeded from the order, the same as the
 *               untracked row's "Add stock".
 *
 * Neither carries `deliveredOnSave`: those flows use the save to settle an
 * order nobody had ticked, and this one is only reached because the order has
 * already been recorded as arrived.
 *
 * Null when there is nothing to prefill — a multi-material truck (see below),
 * or a line with no material name: an empty dialog is worse than none.
 */
export function buildDeliveredStockDialog(
  delivery: Delivery,
  levels: InventoryLevel[],
  deliveries: Delivery[],
): IStockDialogState | null {
  /* One material, one dialog. A truck carrying several lands several
     materials at once, and the reconciliation is per material — which is left
     to each Inventory row's own "did it arrive?" ask (the backend flags every
     line's material from the same `deliveredAt`), rather than picking one of
     the three arbitrarily and calling the arrival handled. */
  if (delivery.lines.length !== 1) return null;
  const line = firstLine(delivery);
  if (line.itemName.trim() === '') return null;
  const key = fold(line.itemName);
  const level = levels.find((l) => fold(l.materialName) === key);
  if (level) {
    const sync = buildSyncStockDialog(level, deliveries);
    // `withArrival`, not a re-run of `arrivedSince` with `delivery` appended:
    // the delivery being folded in is one the page's cached list may not
    // describe as arrived yet — the PATCH has only just returned — so it is
    // added from the record in hand rather than waited for. `withArrival`'s
    // own ref dedupe covers the case where the cache already has it.
    return { ...sync, arrived: withArrival(sync.arrived ?? null, delivery, line) };
  }
  const { deliveredOnSave: _alreadyArrived, ...add } = buildAddStockDialog({
    materialName: line.itemName,
    quantity: line.quantity,
    unit: line.unit,
    deliveryRef: delivery.ref,
    deliveredAt: delivery.deliveredAt,
    isSynced: false,
    dueDate: delivery.dueDate,
    // No lead on the prefill, and none needed: `openingLead` falls through to
    // the dialog's `leadsByMaterial`, which the page builds from the untracked
    // list — so this material's own measured lead (and its caption) is found
    // there, exactly as it is from the untracked row's own Add-stock.
    leadTimeDays: null,
    leadSource: null,
  });
  return add;
}

/**
 * The orders a confirmed Save records as arrived (`deliveredOnSave`) — sent as
 * the snapshot's own `arrivals`, so the server marks them Delivered only when
 * it takes the count, and just before it. Deliberately nothing else: an ETA
 * autofill on sync was ruled in and withdrawn the same day (Niv, 2026-08-31)
 * — sync never writes a `dueDate`.
 */
export function syncArrivals(stock: IStockDialogState): string[] {
  return stock.deliveredOnSave != null ? [stock.deliveredOnSave] : [];
}
